import { motion } from 'framer-motion'
import { Building2, Cpu, ShoppingCart, Stethoscope, Building, DollarSign } from 'lucide-react'

const industries = [
    {
        icon: Building2,
        name: 'Enterprise Software',
        description: 'Embed intelligent agents into your SaaS products to deliver personalized, automated user experiences.',
        useCases: [
            'Automated customer onboarding',
            'Intelligent documentation search',
            'Predictive analytics and insights',
        ],
    },
    {
        icon: DollarSign,
        name: 'Financial Services',
        description: 'Deploy secure, compliant AI agents for trading, risk management, and customer service.',
        useCases: [
            'Fraud detection and prevention',
            'Algorithmic trading assistants',
            'Regulatory compliance automation',
        ],
    },
    {
        icon: Stethoscope,
        name: 'Healthcare',
        description: 'Leverage AI to improve patient outcomes, streamline operations, and accelerate research.',
        useCases: [
            'Clinical decision support',
            'Medical record summarization',
            'Drug discovery acceleration',
        ],
    },
    {
        icon: ShoppingCart,
        name: 'E-Commerce',
        description: 'Create personalized shopping experiences and optimize supply chain operations with AI agents.',
        useCases: [
            'Personalized product recommendations',
            'Intelligent customer support',
            'Inventory optimization',
        ],
    },
    {
        icon: Cpu,
        name: 'Technology',
        description: 'Build next-generation products powered by LLMs and agent-based architectures.',
        useCases: [
            'Code generation and review',
            'Automated testing and QA',
            'DevOps automation',
        ],
    },
    {
        icon: Building,
        name: 'Manufacturing',
        description: 'Optimize production processes and supply chains with intelligent automation.',
        useCases: [
            'Predictive maintenance',
            'Quality control automation',
            'Supply chain optimization',
        ],
    },
]

const solutions = [
    {
        title: 'Rapid Prototyping',
        description: 'Go from idea to working prototype in days, not months.',
        benefits: ['Visual workflow builder', 'Pre-built templates', 'Instant deployment'],
    },
    {
        title: 'Production Scaling',
        description: 'Scale seamlessly from prototype to millions of users.',
        benefits: ['Auto-scaling infrastructure', 'Load balancing', 'Global CDN'],
    },
    {
        title: 'Enterprise Security',
        description: 'Bank-grade security and compliance for mission-critical applications.',
        benefits: ['SOC 2 Type II', 'GDPR compliant', 'End-to-end encryption'],
    },
]

export const SolutionsPage = () => {
    return (
        <main className="pt-20">
            {/* Hero Section */}
            <section className="py-24 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 -z-10" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent -z-10" />

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                            Solutions for Every
                            <br />
                            <span className="gradient-text">Industry</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-400 max-w-4xl mx-auto">
                            AgentForge adapts to your unique business needs, delivering tailored
                            AI solutions across industries
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Industries Grid */}
            <section className="py-24 bg-slate-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {industries.map((industry, index) => {
                            const Icon = industry.icon
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className="group"
                                >
                                    <div className="h-full p-8 rounded-2xl bg-slate-800/50 border border-slate-700/50 hover:border-primary-500/50 transition-all duration-300 backdrop-blur-sm">
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                            <Icon className="w-7 h-7 text-white" />
                                        </div>

                                        <h3 className="text-2xl font-bold mb-4">{industry.name}</h3>
                                        <p className="text-slate-400 mb-6">{industry.description}</p>

                                        <h4 className="text-sm font-semibold text-primary-400 mb-3 uppercase tracking-wide">
                                            Use Cases
                                        </h4>
                                        <ul className="space-y-2">
                                            {industry.useCases.map((useCase, ucIndex) => (
                                                <li
                                                    key={ucIndex}
                                                    className="flex items-start space-x-2 text-sm text-slate-300"
                                                >
                                                    <span className="text-primary-400 mt-0.5">•</span>
                                                    <span>{useCase}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Solutions Approach */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Our Solution Approach
                        </h2>
                        <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                            From concept to production, we support you at every stage
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {solutions.map((solution, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-800/30 border border-slate-700/50"
                            >
                                <h3 className="text-2xl font-bold mb-4">{solution.title}</h3>
                                <p className="text-slate-400 mb-6">{solution.description}</p>
                                <ul className="space-y-3">
                                    {solution.benefits.map((benefit, bIndex) => (
                                        <li
                                            key={bIndex}
                                            className="flex items-start space-x-2 text-slate-300"
                                        >
                                            <svg
                                                className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            <span>{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-slate-900/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Let's Build Your Solution
                        </h2>
                        <p className="text-xl text-slate-400 mb-8">
                            Our team is ready to help you leverage AI for your specific needs
                        </p>
                        <button className="px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40">
                            Contact Our Team
                        </button>
                    </motion.div>
                </div>
            </section>
        </main>
    )
}

